"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SliderBaseWidget = exports.HandleType = void 0;
var tslib_1 = require("tslib");
var React = require("react");
var SliderWidget_1 = require("./SliderWidget");
var SliderHandleContainer_1 = require("./SliderHandleContainer");
var SliderTickBarWidget_1 = require("./SliderTickBarWidget");
var SliderTrackWidget_1 = require("./SliderTrackWidget");
var SliderValueLogic_1 = require("./SliderValueLogic");
var SliderStyles_1 = require("./SliderStyles");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var HandleType;
(function (HandleType) {
    HandleType["Both"] = "both";
    HandleType["Left"] = "left";
    HandleType["Right"] = "right";
})(HandleType = exports.HandleType || (exports.HandleType = {}));
/**
 * A horizontal slider widget with one or two handles.
 */
var SliderBaseWidget = /** @class */ (function (_super) {
    tslib_1.__extends(SliderBaseWidget, _super);
    function SliderBaseWidget(props) {
        var _this = _super.call(this, props) || this;
        _this.getTickCount = function () {
            // And now for a word on how the ticks and handle manage to render all aligned and the handle(s) snaps to ticks:
            //
            // * The way SliderValueLogic does snapping via Math.round, so the number of integers from min to max become the
            //   number of snap points and by the magic of math are evenly distributed horizontally.
            // * The tick rendering in SliderTickBarWidget evenly distributes ticks horizontally as well, and the number of
            //   ticks also equals the number of integers from min to max.
            // * In other words, there are the same number of snap spots and number of ticks, and both are evenly distributed
            //   horizontally.
            // * Finally, to get the ticks to perfectly line up with the handle(s) (and the press indicator on the handle) we
            //   just have to make sure that the horizontal areas for the ticks and the handle start at the same x position of
            //   the page and are the same width.  This is controlled by the styles defined in SliderStyles.
            return !!_this.props.isDiscrete ? Math.floor(_this.props.max) - Math.ceil(_this.props.min) + 1 : 0;
        };
        _this.getIndicatorAndDataBarPositions = function (leftHandlePosition, rightHandlePosition) {
            if (isSingleHandleSlider(_this.props.value) && !_this.props.isDiscrete) {
                if (_this.props.openEndedAt === SliderWidget_1.OpenEndedAt.Min) {
                    return { indicatorBarPosition: { start: 0, end: leftHandlePosition } };
                }
                else if (_this.props.openEndedAt === SliderWidget_1.OpenEndedAt.Max) {
                    return { indicatorBarPosition: { start: leftHandlePosition, end: 1 } };
                }
            }
            else if (!isSingleHandleSlider(_this.props.value)) {
                var dataBarPosition = undefined;
                if (_this.props.trackBarDataRange) {
                    var dataBarStartPosition = _this.sliderValueLogic.calculateSliderPositionAsProportion(_this.props.trackBarDataRange.from);
                    var dataBarEndPosition = _this.sliderValueLogic.calculateSliderPositionAsProportion(_this.props.trackBarDataRange.to);
                    dataBarPosition = {
                        start: Math.max(leftHandlePosition, dataBarStartPosition),
                        end: Math.min(rightHandlePosition, dataBarEndPosition) || undefined,
                    };
                }
                return { indicatorBarPosition: { start: leftHandlePosition, end: rightHandlePosition }, dataBarPosition: dataBarPosition };
            }
            return {};
        };
        /**
         * A convenience method to fire the onChange method from props for when a single value (i.e. handle) has changed.
         */
        _this.fireOnChangeForSingleValue = function (handle, newValue) {
            if (!_this.props.onChange) {
                return;
            }
            if (isSingleHandleSlider(_this.props.value)) {
                if (newValue !== _this.props.value) {
                    _this.props.onChange(newValue);
                }
            }
            else {
                if (handle === HandleType.Left && newValue !== _this.props.value.from) {
                    _this.props.onChange({ from: Math.min(newValue, _this.props.value.to), to: _this.props.value.to });
                }
                else if (handle === HandleType.Right && newValue !== _this.props.value.to) {
                    _this.props.onChange({ from: _this.props.value.from, to: Math.max(newValue, _this.props.value.from) });
                }
            }
        };
        /**
         * A convenience method to fire the onChange method from props for when both values have changed (i.e. both handles).
         */
        _this.fireOnChangeForTwoValues = function (newFromValue, newToValue) {
            if (!_this.props.onChange || isSingleHandleSlider(_this.props.value) || newFromValue > newToValue) {
                return;
            }
            _this.props.onChange({ from: newFromValue, to: newToValue });
        };
        _this.handleHandleDragStart = function (handle, position) {
            _this.setState({ areHandlesBeingDragged: true });
            var newValue = _this.sliderValueLogic.calculateNewSliderValueFromProportion(position);
            _this.fireOnChangeForSingleValue(handle, newValue);
        };
        _this.handleHandleDragMove = function (handle, position) {
            var newValue = _this.sliderValueLogic.calculateNewSliderValueFromProportion(position);
            _this.fireOnChangeForSingleValue(handle, newValue);
        };
        _this.handleBothHandleDragStart = function () {
            _this.setState({ areHandlesBeingDragged: true });
        };
        _this.handleBothHandleDragMove = function (leftHandlePosition, rightHandlePosition) {
            var newFromValue = _this.sliderValueLogic.calculateNewSliderValueFromProportion(leftHandlePosition);
            var newToValue = _this.sliderValueLogic.calculateNewSliderValueFromProportion(rightHandlePosition);
            _this.fireOnChangeForTwoValues(newFromValue, newToValue);
        };
        _this.handleHandleRelease = function () {
            _this.setState({ areHandlesBeingDragged: false });
        };
        _this.handleHandleKeyDown = function (handle, key) {
            _this.setState({ areHandlesBeingDragged: true });
            if (handle === HandleType.Both) {
                if (!isSingleHandleSlider(_this.props.value)) {
                    var newSliderRangeValue = _this.sliderValueLogic.calculateNewSliderRangeValueForKey(key, _this.props.value);
                    if (newSliderRangeValue) {
                        _this.fireOnChangeForTwoValues(newSliderRangeValue.from, newSliderRangeValue.to);
                    }
                }
            }
            else {
                var oldValue = void 0;
                if (isSingleHandleSlider(_this.props.value)) {
                    oldValue = _this.props.value;
                }
                else {
                    oldValue = handle === HandleType.Left ? _this.props.value.from : _this.props.value.to;
                }
                var newValue = _this.sliderValueLogic.calculateNewSliderValueForKey(key, oldValue);
                if (newValue !== undefined) {
                    _this.fireOnChangeForSingleValue(handle, newValue);
                }
            }
        };
        _this.handleRangeHandleFocus = function (focus) {
            _this.setState({ isRangeHandleFocused: focus });
        };
        _this.validateProps(props);
        _this.state = { areHandlesBeingDragged: false, isRangeHandleFocused: false };
        _this.sliderValueLogic = new SliderValueLogic_1.SliderValueLogic(_this.props.min, _this.props.max, !!_this.props.isDiscrete);
        return _this;
    }
    SliderBaseWidget.prototype.validateProps = function (props) {
        // tslint:disable-next-line:deprecation
        var ariaLabel = props.ariaLabel, ariaAttributes = props.ariaAttributes, min = props.min, max = props.max, value = props.value;
        if (!ariaLabel && !ariaAttributes) {
            throw new Error("Either ariaAttributes or ariaLabel must be provided.");
        }
        if (isNaN(min) || isNaN(max) ||
            (isSingleHandleSlider(value) && isNaN(value)) ||
            (!isSingleHandleSlider(value) && (isNaN(value.from) || isNaN(value.to)))) {
            throw new Error("NaN is not a valid value for min, max, or value(s).");
        }
    };
    SliderBaseWidget.prototype.componentDidUpdate = function (prevProps, prevState) {
        this.validateProps(this.props);
        var _a = this.props, isDiscrete = _a.isDiscrete, max = _a.max, min = _a.min, onHandleRelease = _a.onHandleRelease;
        if (isDiscrete !== prevProps.isDiscrete || min !== prevProps.min || max !== prevProps.max) {
            this.sliderValueLogic = new SliderValueLogic_1.SliderValueLogic(min, max, !!isDiscrete);
            this.forceUpdate();
        }
        if (prevState.areHandlesBeingDragged && !this.state.areHandlesBeingDragged) {
            if (onHandleRelease) {
                onHandleRelease();
            }
            else if (prevProps.onHandleRelease) {
                prevProps.onHandleRelease();
            }
        }
    };
    SliderBaseWidget.prototype.getSliderHandleAriaAttributes = function (value, handleType) {
        var ariaAttributes = tslib_1.__assign(tslib_1.__assign({}, (handleType === HandleType.Right ? this.props.ariaAttributes.rightHandle : this.props.ariaAttributes.leftHandle)), { 'aria-valuemax': this.props.isDiscrete ? Math.floor(this.props.max) : this.props.max, 'aria-valuemin': this.props.isDiscrete ? Math.ceil(this.props.min) : this.props.min, 'aria-valuenow': value });
        // tslint:disable:deprecation
        if (!ariaAttributes['aria-label'] && this.props.ariaLabel) {
            // this prop is deprecated and its behaivor is *wrong* per WAI-ARIA spec -- this is just here to preserve the old prop behavior
            ariaAttributes['aria-label'] = this.props.ariaLabel;
        }
        // tslint:enable:deprecation
        return ariaAttributes;
    };
    SliderBaseWidget.prototype.render = function () {
        var styles = SliderStyles_1.getSliderStyles();
        var leftHandlePosition = undefined;
        var leftHandleAriaAttributes = undefined;
        var rightHandlePosition = undefined;
        var rightHandleAriaAttributes = undefined;
        if (isSingleHandleSlider(this.props.value)) {
            leftHandlePosition = this.sliderValueLogic.calculateSliderPositionAsProportion(this.props.value);
            leftHandleAriaAttributes = this.getSliderHandleAriaAttributes(this.props.value, HandleType.Left);
        }
        else {
            leftHandlePosition = this.sliderValueLogic.calculateSliderPositionAsProportion(this.props.value.from);
            leftHandleAriaAttributes = this.getSliderHandleAriaAttributes(this.props.value.from, HandleType.Left);
            rightHandlePosition = this.sliderValueLogic.calculateSliderPositionAsProportion(this.props.value.to);
            rightHandleAriaAttributes = this.getSliderHandleAriaAttributes(this.props.value.to, HandleType.Right);
        }
        var tickCount = this.getTickCount();
        var indicatorAndDataBarPositions = this.getIndicatorAndDataBarPositions(leftHandlePosition, rightHandlePosition);
        return (React.createElement("div", tslib_1.__assign({ "data-tb-test-id": this.props.testId, style: tslib_1.__assign(tslib_1.__assign({}, styles.OuterContainer), this.props.style) }, ReactHelpers_1.EventUtilsOptOut),
            React.createElement("div", { style: styles.TrackAndHandleContainer },
                tickCount > 1 && React.createElement(SliderTickBarWidget_1.SliderTickBarWidget, { testId: this.props.testId, tickCount: tickCount }),
                React.createElement(SliderTrackWidget_1.SliderTrackWidget, { dataBarPosition: indicatorAndDataBarPositions.dataBarPosition, handleIsBeingDragged: this.state.areHandlesBeingDragged, isRangeHandleFocused: this.state.isRangeHandleFocused, rangeIndicatorBarPosition: indicatorAndDataBarPositions.indicatorBarPosition, testId: this.props.testId }),
                React.createElement(SliderHandleContainer_1.SliderHandleContainer, { disabled: this.props.disabled, handles: { leftHandlePosition: leftHandlePosition, rightHandlePosition: rightHandlePosition }, handlesAriaAttributes: { leftHandleAriaAttributes: leftHandleAriaAttributes, rightHandleAriaAttributes: rightHandleAriaAttributes }, onBothHandleDragMove: this.handleBothHandleDragMove, onBothHandleDragStart: this.handleBothHandleDragStart, onHandleDragMove: this.handleHandleDragMove, onHandleDragStart: this.handleHandleDragStart, onHandleKeyDown: this.handleHandleKeyDown, onHandleRelease: this.handleHandleRelease, onRangeHandleFocus: this.handleRangeHandleFocus, testId: this.props.testId }))));
    };
    SliderBaseWidget.defaultProps = {
        ariaAttributes: { leftHandle: undefined, rightHandle: undefined },
    };
    return SliderBaseWidget;
}(React.Component));
exports.SliderBaseWidget = SliderBaseWidget;
/**
 * If this is true, then the slider should have a single handle. Conversely, if false, it should have two handles.
 */
var isSingleHandleSlider = function (value) {
    return typeof value === 'number';
};
//# sourceMappingURL=SliderBaseWidget.js.map